// What a laser can cut is decided by the engraving fonts, not by what a phone
// keyboard will happily insert. An emoji is the clearest case: it has no outline
// in any of our fonts, and it is drawn by the operating system, so the customer
// approves a mockup showing Apple's artwork and the engraving could never match
// it on any machine. Order #AU#3641 shipped exactly that way.
//
// Two gates, cheapest first. This module is the structural one, which needs no
// fonts and can run on every keystroke. The font-coverage gate lives in
// personalize-product.js, because it needs the loaded font objects.

/**
 * The symbols the picker offers. Allowed even when the chosen font lacks them,
 * because a borrowed diamond reads as intentional while a borrowed letter does not.
 */
export const PICKER_SYMBOLS = ['♥', '☺', '♦', '♣', '♠'];

// Controls, zero-width marks, bidi overrides, variation selectors and skin tones.
// Every modern emoji sequence is glued together from these, none of them is in a
// font, and being invisible they would fail with nothing on screen to point at.
// Kept as ranges rather than a character class so the source stays readable.
const INVISIBLE_RANGES = [
    [0x0000, 0x001f], // C0 controls
    [0x007f, 0x009f], // DEL and C1 controls
    [0x200b, 0x200f], // zero width space, joiners, LTR/RTL marks
    [0x202a, 0x202e], // bidi embedding and overrides
    [0x2060, 0x2064], // word joiner and invisible operators
    [0xfe00, 0xfe0f], // variation selectors, including the emoji presentation one
    [0x1f3fb, 0x1f3ff] // skin tone modifiers
];

// Letters and digits in any script, plus a short punctuation list. Emoji,
// pictographs, dingbats, arrows and the rest of the symbol planes are absent on
// purpose. Combining marks are excluded too: input is normalised to NFC first, so
// an accent arrives as one precomposed character the fonts actually carry.
const STRUCTURAL_RE = /^[\p{L}\p{N} .,'’\-&()/!?:+@#*"]$/u;

function isInvisible(ch) {
    const cp = ch.codePointAt(0);
    return INVISIBLE_RANGES.some(([lo, hi]) => cp >= lo && cp <= hi);
}

/**
 * @param {string} text
 * @returns {string} NFC-normalised, with the invisible characters removed.
 */
export function normalise(text) {
    const nfc = typeof text.normalize === 'function' ? text.normalize('NFC') : text;
    return [...nfc].filter((ch) => !isInvisible(ch)).join('');
}

/**
 * @param {string} ch A single character.
 * @returns {boolean} Whether it is the kind of thing we engrave at all.
 */
export function isStructurallyAllowed(ch) {
    return PICKER_SYMBOLS.includes(ch) || STRUCTURAL_RE.test(ch);
}

/**
 * Drop anything that could never be engraved, and report what went.
 *
 * @param {string} text
 * @returns {{ text: string, removed: string[] }} `removed` is de-duplicated and in
 *   the order the characters first appeared, ready to show to the customer.
 */
export function filterStructural(text) {
    const kept = [];
    const removed = [];
    for (const ch of normalise(text)) {
        if (isStructurallyAllowed(ch)) kept.push(ch);
        else if (!removed.includes(ch)) removed.push(ch);
    }
    return { text: kept.join(''), removed };
}

/**
 * Split what survives the structural gate into what the chosen font can draw,
 * what only another font can draw, and what nothing can draw.
 *
 * Letters have to come from the chosen font: a Georgia letter sitting inside an
 * Engravers MT word reads as a mistake. The picker symbols may come from anywhere.
 *
 * @param {string} text
 * @param {{name: string, font: Object}} primary
 * @param {Array<{name: string, font: Object}>} all Every engraving font, primary included.
 * @returns {{ unsupported: string[], elsewhere: Array<{ char: string, fonts: string[] }> }}
 */
export function classifyAgainstFonts(text, primary, all) {
    const has = (entry, ch) => !!entry?.font?.hasChar?.(ch);
    const unsupported = [];
    const elsewhere = [];

    for (const ch of new Set([...text])) {
        if (/\s/.test(ch)) continue;

        const anywhere = all.filter((entry) => has(entry, ch));
        if (!anywhere.length) {
            unsupported.push(ch);
            continue;
        }
        // A symbol borrowed from another font is the intended behaviour.
        if (PICKER_SYMBOLS.includes(ch)) continue;
        if (!has(primary, ch)) {
            elsewhere.push({ char: ch, fonts: anywhere.map((entry) => entry.name) });
        }
    }

    return { unsupported, elsewhere };
}
